import { ContentFeedbackHandler } from '@droplet-tech-code/core-elements/module/ui/Feedback/Feedback.Handler';
import { LoadingIndicator } from '@droplet-tech-code/core-elements/module/ui/Loading';
import { HStack, VStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  copyToClipboard,
  isNativeMobile,
  isWeb,
} from '@droplet-tech-code/core-elements/module/utils/utils.helper';

import { bookingApi } from '~/screens/Support/Bookings/Bookings.api';
import { translate } from '~/utils/translation.utils';

import { CardWithHeader } from '../Card/CardWithHeader';
import { Field } from '../Card/Field';
import { LiveDateText } from '../DateText';
import { LinkText } from '../LinkText';
import { SwitchStack } from '../Stack/SwitchStack';
import { AppText } from '../Text';
import { BookingMenu } from './BookingPopover.utils';
import { usePaymentSelector } from './Modals/PaymentSessionSelector';
import { IconButton } from '@droplet-tech-code/core-elements/module/ui/Button';
import { Icon } from '@droplet-tech-code/core-elements/module/ui/Icon';
import { useUpdateBookingModal } from './Modals/BookingUpdate';
import { Pill } from '../Pill/Pill';

export const BookingInfo = ({
  bookingId,
  supportId,
  isPrioritySupport,
}: {
  bookingId?: string;
  supportId?: string;
  isPrioritySupport?: boolean;
}) => {
  const {
    currentData,
    data,
    isLoading: isLoadingBookingData,
    isError,
  } = bookingApi.useGetBookingQuery(bookingId ?? '', { skip: !bookingId });

  const { open: openPaymentSession } = usePaymentSelector();
  const { open: openUpdateModal } = useUpdateBookingModal(
    bookingId || '',
    supportId || '',
  );

  const bookingInfo = data?.booking;

  if (bookingId === undefined) {
    return null;
  }

  if (isLoadingBookingData) {
    return (
      <CardWithHeader
        headerText={`${translate('bookings.bookingInfo')}`}
        w={isWeb ? 600 : undefined}
      >
        <View p="2.5">
          <LoadingIndicator />
        </View>
      </CardWithHeader>
    );
  }

  if (bookingInfo === undefined || (!currentData && isError)) {
    return (
      <CardWithHeader
        headerText={`${translate('bookings.bookingInfo')}`}
        w={isWeb ? 600 : undefined}
      >
        <VStack p="2.5">
          <ContentFeedbackHandler
            data={currentData}
            removeTopMargin
            isLoading={isLoadingBookingData}
            feedback={`${translate('bookings.bookingNotFound')} ${bookingId ?? 'NA'}`}
            EmptyDataFeedBack={<View />}
          >
            {<></>}
          </ContentFeedbackHandler>
        </VStack>
      </CardWithHeader>
    );
  }
  const {
    p_email,
    p_firstName,
    p_lastName,
    p_phone,
    lang,
    paymentSessions,
    dateCreated,
    booking_ref,
  } = bookingInfo;

  return (
    <CardWithHeader
      headerText={`${translate('bookings.bookingInfo')}`}
      rightChild={() => {
        return (
          <HStack space="2" align="center">
            {isPrioritySupport && (
              <Pill color="warning-extraLight" style={{ backgroundColor: '#FDF1CE' }}>
                <HStack space="1" align="center">
                  <Icon icon="star-filled" size="small" color="warning-mid" />
                  <AppText.MiscTag bold color="warning-mid">
                    {translate('supportRequests.prioritySupport')}
                  </AppText.MiscTag>
                </HStack>
              </Pill>
            )}
            <IconButton
              color="monochrome"
              icon="edit"
              onPress={() => openUpdateModal(bookingId)}
            />
            {BookingMenu({ booking: bookingInfo, supportId })}
          </HStack>
        );
      }}
      w={isWeb ? 600 : undefined}
    >
      <SwitchStack p="2.5" space={isNativeMobile ? '1' : '2.5'}>
        <VStack flex={isNativeMobile ? undefined : 1} space="1">
          <HStack flex={1}>
            <AppText.ExtraSmall flex={1} color="monochrome-extraDark" type="small">
              {translate('bookings.nameUpper')}
            </AppText.ExtraSmall>
            <Text.Small>{`${p_firstName}, ${p_lastName}`}</Text.Small>
          </HStack>
          <HStack>
            <Field
              label={translate('bookings.emailUpper')}
              value={p_email}
              isCopyable
              valueType="small"
            />
          </HStack>
          <HStack>
            <AppText.ExtraSmall flex={1} color="monochrome-extraDark" type="small">
              {translate('bookings.phoneUpper')}
            </AppText.ExtraSmall>
            <Text.Small
              onPress={() => {
                copyToClipboard(p_phone);
              }}
              align="right"
            >
              {p_phone}
            </Text.Small>
          </HStack>
          <HStack flex={1} />
        </VStack>
        <VStack flex={isNativeMobile ? undefined : 1} space="1">
          <HStack>
            <AppText.ExtraSmall flex={1} color="monochrome-extraDark" type="small">
              {translate('bookings.languageUpper')}
            </AppText.ExtraSmall>
            <Text.Small align="right">{`${lang}`}</Text.Small>
          </HStack>
          <HStack>
            <AppText.ExtraSmall flex={1} color="monochrome-extraDark" type="small">
              {translate('bookings.paymentSessions')}
            </AppText.ExtraSmall>
            <View align="flex-end">
              <LinkText
                onPress={() => {
                  openPaymentSession(bookingInfo);
                }}
              >{`${paymentSessions.length} sessions`}</LinkText>
            </View>
          </HStack>
          <HStack space="2.5" style={{ overflow: 'hidden' }}>
            <AppText.ExtraSmall flex={1} color="monochrome-extraDark" type="small">
              {translate('bookings.createdUpper')}
            </AppText.ExtraSmall>
            <LiveDateText
              align="right"
              dateTime={dateCreated}
              formatOption="lifetimeDependent&fullDateTime"
              type="small"
              isCopyable
            />
          </HStack>
          <HStack>
            <Field
              label={translate('bookings.bookingRef')}
              value={booking_ref}
              isCopyable
              valueType="small"
            />
          </HStack>
        </VStack>
      </SwitchStack>
    </CardWithHeader>
  );
};
